import React from 'react';
import { Box, Skeleton, Card, CardContent, Grid, SxProps, Theme } from '@mui/material';

interface ShimmerProps {
  variant?: 'text' | 'rectangular' | 'circular';
  width?: number | string;
  height?: number | string;
  animation?: 'pulse' | 'wave' | false;
  sx?: SxProps<Theme>;
}

export const Shimmer: React.FC<ShimmerProps> = ({ 
  variant = 'rectangular', 
  width = '100%', 
  height = 20, 
  animation = 'wave',
  sx
}) => {
  return (
    <Skeleton
      variant={variant}
      width={width}
      height={height}
      animation={animation}
      sx={{
        backgroundColor: 'rgba(0, 0, 0, 0.06)',
        '&::after': {
          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
        },
        ...sx,
      }}
    />
  );
};

export const DashboardShimmer: React.FC = () => {
  return (
    <Box>
      {/* Header Shimmer */}
      <Box sx={{ mb: 4 }}>
        <Shimmer width={200} height={40} />
        <Shimmer width={400} height={20} sx={{ mt: 1 }} />
      </Box>

      {/* Stats Cards Shimmer */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[1, 2, 3, 4].map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Shimmer variant="circular" width={48} height={48} />
                  <Box sx={{ ml: 2, flex: 1 }}>
                    <Shimmer width="80%" height={20} />
                  </Box>
                </Box>
                <Shimmer width="60%" height={32} />
                <Shimmer width="40%" height={16} sx={{ mt: 1 }} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Main Content Shimmer */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
            <CardContent sx={{ p: 3 }}>
              <Shimmer width={200} height={24} sx={{ mb: 3 }} />
              {[1, 2, 3, 4, 5].map((item) => (
                <Box key={item} sx={{ mb: 2 }}>
                  <Shimmer width="100%" height={60} />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Shimmer width={150} height={24} sx={{ mb: 2 }} />
                  {[1, 2, 3].map((item) => (
                    <Box key={item} sx={{ mb: 1 }}>
                      <Shimmer width="100%" height={40} />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export const TableShimmer: React.FC<{ rows?: number; columns?: number }> = ({ 
  rows = 5, 
  columns = 6 
}) => {
  return (
    <Box>
      {/* Table Header Shimmer */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, p: 2 }}>
        {Array.from({ length: columns }).map((_, index) => (
          <Shimmer key={index} width={120} height={20} />
        ))}
      </Box>
      
      {/* Table Rows Shimmer */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <Box key={rowIndex} sx={{ display: 'flex', gap: 2, mb: 1, p: 2 }}>
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Shimmer key={colIndex} width={100} height={20} />
          ))}
        </Box>
      ))}
    </Box>
  );
};

export const UserProfileShimmer: React.FC = () => {
  return (
    <Box>
      {/* Header Shimmer */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Shimmer variant="circular" width={40} height={40} />
        <Box sx={{ flex: 1 }}>
          <Shimmer width={300} height={32} />
          <Shimmer width={200} height={20} sx={{ mt: 1 }} />
        </Box>
        <Shimmer width={120} height={36} />
      </Box>

      {/* Profile Card Shimmer */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3} sx={{ textAlign: 'center' }}>
              <Shimmer variant="circular" width={120} height={120} sx={{ mx: 'auto', mb: 2 }} />
              <Shimmer width={150} height={24} sx={{ mx: 'auto', mb: 1 }} />
              <Shimmer width={100} height={20} sx={{ mx: 'auto', mb: 1 }} />
              <Shimmer width={80} height={24} sx={{ mx: 'auto', mb: 1 }} />
              <Shimmer width={60} height={24} sx={{ mx: 'auto' }} />
            </Grid>
            <Grid item xs={12} md={9}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  {[1, 2, 3].map((item) => (
                    <Box key={item} sx={{ mb: 2 }}>
                      <Shimmer width="100%" height={20} />
                    </Box>
                  ))}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {[1, 2, 3].map((item) => (
                    <Box key={item} sx={{ mb: 2 }}>
                      <Shimmer width="100%" height={20} />
                    </Box>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs Shimmer */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <Shimmer key={item} width={120} height={40} />
            ))}
          </Box>
        </Box>
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Shimmer width={200} height={24} sx={{ mb: 2 }} />
              {[1, 2, 3, 4, 5, 6].map((item) => (
                <Shimmer key={item} width="100%" height={56} sx={{ mb: 2 }} />
              ))}
            </Grid>
            <Grid item xs={12} md={6}>
              <Shimmer width={200} height={24} sx={{ mb: 2 }} />
              {[1, 2, 3, 4, 5, 6].map((item) => (
                <Shimmer key={item} width="100%" height={56} sx={{ mb: 2 }} />
              ))}
            </Grid>
          </Grid>
        </Box>
      </Card>
    </Box>
  );
};

export default Shimmer;
